import {
  CompleteRegistrationProps,
  CompleteUserRegistration,
  GETInvitationStatus,
  POSTResendInvitation,
} from '@/apis/users/requests.ts'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { EyeIcon, EyeSlashIcon } from '@/app/common/icons/general'
import { REQUEST_STATUS } from '@/app/common/types_and_interfaces'
import { cn } from '@/app/common/utilities'
import { AuthLayout } from '@/app/pages/authentication/layout.tsx'
import { Button, buttonVariants } from '@/common/components/Button.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Input } from '@/common/components/Input.tsx'
import { global_error_codes } from '@/common/constants.ts'
import { sleep } from '@/common/utilities.ts'
import { getPublicPrograms } from '@/queries/programs/requests'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { AppDispatch, RootState } from '@/storage/store'
import { POSTLogin } from '@/storage/user/authSessionSlice'
import TermsPDF from '@assets/ACE_Terms_of_Use.pdf'
import TermsPDFes from '@assets/Términos_de_Uso_de_las_Becas_ACE.pdf'
import AceScholarshipsLogo from '@assets/AceScholarshipLogo1.jpg'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { ChangeEvent, useEffect, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

const UserRegistrationSchema = z
  .object({
    email: z.string().email({ message: 'please_enter_a_valid_email_address' }),
    password: z
      .string()
      .min(8, { message: 'password_must_be_at_least_characters_long' })
      .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/, {
        message: 'password_must_contain_at_least_one_uppercase_letter',
      }),
    confirm_password: z.string().min(8, {
      message: 'confirm_password_must_be_at_least_characters_long',
    }),
    terms_accepted: z.literal(true, {
      errorMap: () => ({ message: 'you_must_accept_the_terms_and_conditions' }),
    }),
    phone: z.string().min(8, 'Please enter a valid phone number'),
    user_has_accepted_text_notifications: z.boolean(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'passwords_do_not_match',
    path: ['confirm_password'],
  })
type UserRegistrationForm = z.infer<typeof UserRegistrationSchema>

export default function RegistrationInvite() {
  const { t: translate, i18n } = useTranslation()
  const [requestInFlight, setRequestInFlight] = useState(false)
  const [searchParams] = useSearchParams()
  const [invitation, setInvitation] = useState<null | QueryInvitation>(null)
  const { fireAlert } = useAppAlert()
  const navigateTo = useNavigate()
  const AuthState = useSelector((state: RootState) => state.AuthSession)
  const AuthRequestState = AuthState.request
  const asyncDispatch = useDispatch<AppDispatch>()
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)
  const query = useQuery({
    queryFn: getPublicPrograms,
    queryKey: ['public_programs'],
  })

  const termsLink = i18n.language === 'es' ? TermsPDFes : TermsPDF

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    control,
  } = useForm<UserRegistrationForm>({
    defaultValues: {
      phone: '',
      confirm_password: '',
      email: '',
      password: '',
      user_has_accepted_text_notifications: true,
    },
    resolver: zodResolver(UserRegistrationSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhone(e.target.value)
    setValue('phone', formattedValue)
  }

  const formatPhone = (phoneNumber: string) => {
    let value = phoneNumber.replace(/\D/g, '')
    if (value.length > 10) value = value.slice(0, 10)

    const formattedValue = value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    return formattedValue
  }

  useEffect(() => {
    async function processInvitation() {
      if (hasRegistered()) {
        navigateTo(AppRoutePaths.home.index)
        return
      }

      decodeBase64Invitation(searchParams.get('q') ?? '')
        .then(async (qInvitation) => {
          const response = await GETInvitationStatus(
            qInvitation.email,
            qInvitation.token
          )
          setInvitation(qInvitation)
          setValue('email', qInvitation.email)

          if (response.error) {
            if (response.error.code == global_error_codes.invitation_expired) {
              const userInput = await fireAlert({
                variant: 'error',
                confirmBtnText: translate('resend'),
                denyBtn: true,
                denyBtnText: translate('cancel'),
                swapButtons: true,
                message: response.error.message,
              })
              if (userInput.confirmed) {
                const callBackUrl =
                  window.location.origin +
                  AppRoutePaths.auth.registration_invite
                const reSendResponse = await POSTResendInvitation({
                  email: qInvitation.email,
                  invitation_token: qInvitation.token,
                  call_back_url: callBackUrl,
                  program_id: parseInt(qInvitation.program_id),
                })
                if (reSendResponse.error) {
                  await fireAlert({
                    variant: 'error',
                    message: response.error.message,
                  })
                  navigateTo(AppRoutePaths.auth.login)
                }
              }
            } else {
              await fireAlert({
                variant: 'error',
                message: response.error.message,
              })
              navigateTo(AppRoutePaths.auth.login)
            }
          } else {
            setInvitation(qInvitation)
            sessionStorage.setItem('has-registered', 'true')
            setValue('email', qInvitation.email)
          }
        })
        .catch((reason) => {
          console.error(reason)
          fireAlert({
            variant: 'error',
            message: translate('unable_to_retrieve_your_invitation'),
          }).then(() => {})
        })
    }

    processInvitation().then()
  }, [])

  useEffect(() => {
    if (AuthRequestState.status == REQUEST_STATUS.FAILED) {
      setRequestInFlight(false)
      fireAlert({
        variant: 'error',
        message: AuthRequestState.error.message,
      }).then()
      return
    }
    if (AuthRequestState.status === REQUEST_STATUS.SUCCESS) {
      setRequestInFlight(false)
      if (invitation?.type === 'scholar') {
        navigateTo(AppRoutePaths.family_application_renewal.index)
      } else {
        navigateTo(AppRoutePaths.home.index)
      }
      return
    }
  }, [AuthRequestState.status])

  const password = useWatch({ control, name: 'password' })
  const confirmPassword = useWatch({ control, name: 'confirm_password' })

  function hasRegistered() {
    // prevents fireAlerts from being fired again if user has signed up.
    // Not the greatest solution but don't want to break anything.
    return sessionStorage.getItem('has-registered') === 'true'
  }

  const onSubmit: SubmitHandler<UserRegistrationForm> = async (formData) => {
    setRequestInFlight(true)
    const payload: CompleteRegistrationProps = {
      email: formData.email,
      password: formData.password,
      user_has_accepted_text_notifications:
        formData.user_has_accepted_text_notifications,
      phone: formData.phone,
      token: invitation?.token ?? '',
      applicant_id: invitation?.applicant_id
        ? parseInt(invitation.applicant_id)
        : 0,
      fromAdmin: invitation?.type !== 'scholar',
    }
    const response = await CompleteUserRegistration(payload)

    if (response.data) {
      asyncDispatch(
        POSTLogin({
          email: formData.email,
          password: formData.password,
          remember_me: false,
        })
      )
    } else {
      setRequestInFlight(false)
      fireAlert({
        variant: 'error',
        message: response.error.message,
      }).then(() => navigateTo(AppRoutePaths.auth.login))
    }
  }

  if (searchParams.get('q') !== null) {
    if (invitation === null) {
      return (
        <AuthLayout>
          <AuthLayout.Content>
            <div className="flex flex-col items-center space-y-8">
              <span className="flex flex-col items-center font-medium text-gray-600">
                <span>{translate('retrieving_your_invitation')}</span>
                <span>{translate('please_wait')}</span>
              </span>
              <EllipsesLoader size="md" />
            </div>
          </AuthLayout.Content>
        </AuthLayout>
      )
    }

    const programName = query.data?.data.find(
      (program) => program.id === parseInt(invitation.program_id)
    )?.name

    return (
      <AuthLayout>
        <AuthLayout.Content className="space-y-10">
          <div>
            <img
              alt="Ace Logo"
              src={AceScholarshipsLogo}
              className="mx-auto h-auto"
              width="90%"
            />
            <h3 className="mt-3 text-center text-sm text-gray-500">
              {translate('register')}
            </h3>
            <p className="mt-3 text-center text-sm text-gray-500">
              {translate('already_have_an_account')}&nbsp;
              <Link
                to={AppRoutePaths.auth.login}
                className="cursor-pointer text-primary font-semibold"
              >
                {translate('log_in')}
              </Link>
            </p>
          </div>
          <form
            noValidate
            className="space-y-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {translate('email')}
              </label>
              <Input
                {...register('email')}
                type="text"
                required
                readOnly
                autoComplete="home email"
                placeholder="example@gmail.com"
              />
              <FormErrorMessage
                message={translate(errors.email?.message ?? '')}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {translate('password')}
              </label>
              <div className="relative flex">
                <Input
                  {...register('password')}
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  placeholder={translate('enter_password')}
                  />
                <div
                  className="absolute right-0 flex items-center h-full mr-2"
                  onClick={() => {
                    setShowNewPassword(!showNewPassword)
                  }}
                >
                  {showNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
                </div>
              </div>
              <FormErrorMessage
                message={translate(errors.password?.message ?? '')}
              />
            </div>
            <div>
              <label
                htmlFor="confirm_password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {translate('confirm_password')}
              </label>
              <div className="relative flex">
                <Input
                  {...register('confirm_password')}
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  required
                  placeholder={translate("confirm_password")}
                />
                <div
                  className="absolute right-0 flex items-center h-full mr-2"
                  onClick={() => {
                    setShowConfirmNewPassword(!showConfirmNewPassword)
                  }}
                >
                  {showConfirmNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
                </div>
              </div>
              <FormErrorMessage
                message={translate(
                  confirmPassword && password !== confirmPassword
                    ? 'passwords_do_not_match'
                    : errors.confirm_password?.message
                      ? errors.confirm_password.message
                      : ''
                )}
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {translate('phone')}
              </label>
              <div className="relative flex">
                <Input
                  {...register('phone')}
                  maxLength={10}
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="(123) 456-7890"
                  onChange={handlePhoneChange}
                />{' '}
              </div>
              <FormErrorMessage
                message={translate(errors.phone?.message ?? '')}
              />
            </div>
            <div>
              <div className="flex  gap-x-3">
                <Input
                  {...register('user_has_accepted_text_notifications')}
                  id="sms_notifications"
                  type="checkbox"
                  className="h-5 w-5 text-primary-600 focus:ring-primary-600"
                />
                <label
                  htmlFor="sms_notifications"
                  className="block text-sm font-normal leading-6 text-gray-600"
                >
                  {translate('agree_to_tc', {
                    program_name: programName,
                  })}
                </label>
              </div>
            </div>
            <FormErrorMessage
              message={translate(
                errors.user_has_accepted_text_notifications?.message ?? ''
              )}
            />

            <div>
              <div className="flex items-center gap-x-3">
                <Input
                  {...register('terms_accepted')}
                  id="terms_accepted"
                  type="checkbox"
                  className="h-5 w-5 text-primary-600 focus:ring-primary-600"
                />
                <label
                  htmlFor="terms_accepted"
                  className="block text-sm text-md font-medium leading-6 text-gray-900"
                >
                  {' '}
                  {translate('i_accept_the')}{' '}
                  <Link
                    target="_blank"
                    to={termsLink}
                    className={buttonVariants({
                      variant: 'text',
                      size: 'text',
                    })}
                  >
                    {translate('terms_and_conditions')}
                  </Link>
                  .
                </label>
              </div>
              <FormErrorMessage
                message={translate(errors.terms_accepted?.message ?? '')}
              />
            </div>

            <div className="flex">
              <Button
                type="submit"
                className={cn(
                  'grow space-x-2',
                  (!isValid || requestInFlight) &&
                    'bg-gray-400 hover:bg-gray-400'
                )}
                disabled={!isValid || requestInFlight}
              >
                {requestInFlight && <CircleLoader size="sm" />}
                <span>
                  {requestInFlight
                    ? `${translate('registering')}...`
                    : translate('register')}
                </span>
              </Button>
            </div>
          </form>
        </AuthLayout.Content>
      </AuthLayout>
    )
  } else {
    return <Navigate to={AppRoutePaths.auth.login} />
  }
}

const querySchema = z.object({
  applicant_id: z.string(),
  email: z.string().email(),
  token: z.string(),
  program_id: z.string(),
  type: z.enum(['scholar']).optional(),
})
type QueryInvitation = z.infer<typeof querySchema>

async function decodeBase64Invitation(
  base64Str: string
): Promise<QueryInvitation> {
  await sleep(1.3)
  const decodedUrlQueryString = atob(base64Str)

  // Step 2: Parse the query string manually
  const queryObj: { [key: string]: string } = {}
  const queryPairs = decodedUrlQueryString.split('&') // Split pairs by '&'

  queryPairs.forEach((pair) => {
    const [key, value] = pair.split('=') // Split key and value by '='
    if (key && value) {
      queryObj[key] = value // Assign key-value pair to the object
    }
  })

  console.log('Query Object:', queryObj)

  return querySchema.parse(queryObj)
}
